import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowRightIcon, FileTextIcon, type LucideIcon, icons } from 'lucide-react';

import { contentLang } from '@/components/content-language';
import { ContactCta, PageHero } from '@/components/marketing';
import { pageHead } from '@/lib/head';
import { ogImage } from '@/lib/open-graph';
import { isTranslated, legal, policies } from '@/lib/source';
import { m } from '@/paraglide/messages';
import { getLocale } from '@/paraglide/runtime';

export const metadata = () => ({ title: m.legal_title(), description: m.legal_description() });

export const Route = createFileRoute('/legal/')({
  head: () => pageHead({ ...metadata(), canonical: '/legal', image: ogImage('/legal') }),
  component: LegalIndexPage,
});

type Doc = ReturnType<typeof legal.getPages>[number];

const byOrder = (a: Doc, b: Doc) => a.data.order - b.data.order || a.data.title.localeCompare(b.data.title);

/** A document names its icon in front matter by Lucide name; unknown names fall back to a page. */
function DocIcon({ name }: { name: string | undefined }) {
  const Icon = (name && (icons as Record<string, LucideIcon | undefined>)[name]) || FileTextIcon;
  return <Icon className='size-6 text-brand' />;
}

function LegalIndexPage() {
  return (
    <>
      <PageHero eyebrow={m.legal_eyebrow()} title={m.legal_heading()} lead={m.legal_lead()} />

      <section className='mx-auto w-full max-w-310 px-5 pb-16 sm:px-8 sm:pb-22'>
        <h2 className='mb-7 text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
          {m.legal_documents()}
        </h2>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-[repeat(auto-fit,minmax(210px,1fr))]'>
          {legal
            .getPages(getLocale())
            .toSorted(byOrder)
            .map((doc) => (
              <Link
                key={doc.slugs[0]}
                to='/legal/$slug'
                params={{ slug: doc.slugs[0]! }}
                className='border-line flex flex-col gap-2.5 rounded-[20px] border p-6 transition-colors hover:border-brand sm:p-7'
              >
                <DocIcon name={doc.data.icon} />
                <span lang={contentLang(isTranslated(doc))} className='mt-1.5 text-[19px] font-bold'>
                  {doc.data.title}
                </span>
                <span lang={contentLang(isTranslated(doc))} className='text-body leading-relaxed'>
                  {doc.data.description}
                </span>
              </Link>
            ))}
        </div>
      </section>

      <section className='bg-mist'>
        <div className='mx-auto flex w-full max-w-310 flex-wrap gap-14 px-5 py-14 sm:px-8 sm:py-22'>
          <div className='min-w-0 flex-[1_1_320px]'>
            <h2 className='text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
              {m.legal_policies()}
            </h2>
          </div>
          <div className='border-line min-w-0 flex-[1.6_1_480px] border-b'>
            {policies
              .getPages(getLocale())
              .toSorted(byOrder)
              .map((doc) => (
                <Link
                  key={doc.slugs[0]}
                  to='/legal/policies/$slug'
                  params={{ slug: doc.slugs[0]! }}
                  className='border-line flex items-center justify-between gap-4 border-t py-5 text-lg font-semibold transition-colors hover:text-brand'
                >
                  <span lang={contentLang(isTranslated(doc))}>{doc.data.title}</span>
                  <ArrowRightIcon className='size-5 shrink-0 text-brand' />
                </Link>
              ))}
          </div>
        </div>
      </section>

      <ContactCta title={m.legal_cta_title()} text={m.legal_cta_text()} />
    </>
  );
}
